import React, { useCallback, useEffect, useState } from "react";

import { CircularProgress, TextField } from "@mui/material";
import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";

import { AttendantRoleService } from "services/attendantRole";

import {
  ContainerInputsDate,
  ContainerWithButton,
  StyledAutocomplete,
  Container,
  ContainerBody,
} from "./style";
import { DropdownGlobal, IDropDown } from "../../components/DropdownGlobal";
import { usePermissions } from "../../hooks/permission";
import { ContainerInputs } from "../../pages/app/StyleCSS/bodyCreateEdit";
import { FORMS_ADMIN } from "../../utils/entities/permission";
import { AddButton } from "../AddButton";

export type IProcedureType = {
  itemData?: any;
  setAttendantId: (item: any) => unknown;
  selected: any;
  attendantList: Array<any>;
  setAttendantList: (item: any) => unknown;
  modalOpenNewParticipant: () => unknown;
  fetchParticipants: (data: string) => unknown;
  fieldError: boolean;
  hearingTypeId?: number;
  attendantRoleDropSelected?: any;
  attendantRoleDropSelectedIndex?: number;
  attendantRoleName?: string;
  setAttendantRoleDropSelected: (e: number, index?: number) => unknown;
  attendantRoleId?: number;
};

export const ProcedureAttendant: React.FC<IProcedureType> = ({
  setAttendantId,
  modalOpenNewParticipant,
  attendantList,
  setAttendantList,
  fieldError,
  fetchParticipants,
  hearingTypeId,
  attendantRoleDropSelected,
  attendantRoleDropSelectedIndex,
  attendantRoleName,
  setAttendantRoleDropSelected,
  selected,
  attendantRoleId,
}): JSX.Element => {
  const { t } = useTranslation();
  const MAX_CHARS = 34;
  const [loadingAttendant, setLoadingAttendant] = useState(false);
  const [dropDownAttendantRole, setDropDownAttendantRole] = useState<
    IDropDown[]
  >([]);
  const [participant, setParticipant] = useState<string>("");
  const { create } = usePermissions(FORMS_ADMIN.ATTENDANT);

  useEffect(() => {
    (async () => {
      if (hearingTypeId) {
        const response =
          await AttendantRoleService.getAttendantRoleByHearingType(
            hearingTypeId,
          );

        const onlyActives = response?.data?.data?.filter((e: any) => e.active);
        setDropDownAttendantRole(onlyActives);
      }
    })();
  }, [hearingTypeId]);

  const debouncedFetch = useCallback(
    debounce((value: string) => {
      fetchParticipants(value);
    }, 1000),
    [],
  );

  useEffect(() => {
    setLoadingAttendant(false);
  }, [attendantList]);

  return (
    <Container>
      <ContainerBody>
        <ContainerWithButton>
          <ContainerInputsDate hasPermission={create}>
            <StyledAutocomplete
              value={participant}
              $error={!selected && fieldError}
              loading={loadingAttendant}
              options={attendantList || []}
              noOptionsText={t("filter.noOptionsFound")}
              onInputChange={(_e, newInputValue) => {
                const limited = newInputValue.slice(0, MAX_CHARS);
                const display =
                  newInputValue.length > MAX_CHARS ? `${limited}...` : limited;
                setParticipant(display);

                if (limited.trim().length >= 3) {
                  setLoadingAttendant(true);
                  debouncedFetch(limited);
                }
                if (limited.trim().length === 0) {
                  setAttendantList([]);
                }
              }}
              onChange={(_, option: any) => {
                if (!option) {
                  setParticipant("");
                  setAttendantId(null);
                  return;
                }
                const label = option.label ?? String(option.value);
                const limited = label.slice(0, MAX_CHARS);
                const display =
                  label.length > MAX_CHARS ? `${limited}...` : limited;
                setParticipant(display);
                setAttendantId(option.value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("filter.enterNameOrDocument")}
                  variant="outlined"
                  placeholder={t("filter.enterNameOrDocument")}
                  error={!selected && fieldError}
                  helperText={
                    !selected && fieldError
                      ? t("participants.messages.selectRequired")
                      : ""
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingAttendant && (
                          <CircularProgress
                            color="inherit"
                            size={20}
                            style={{
                              display: "inline-flex",
                              marginRight: "4px",
                              verticalAlign: "middle",
                            }}
                          />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </ContainerInputsDate>
          <AddButton
            onClick={() => {
              modalOpenNewParticipant();
            }}
          />
        </ContainerWithButton>

        <ContainerInputs fullWidth>
          <DropdownGlobal
            width="100%"
            id="attedant_role"
            labelName={t("labels.participantType")}
            options={dropDownAttendantRole}
            error={!attendantRoleId && fieldError}
            required
            selected={attendantRoleDropSelected}
            selectedIndex={attendantRoleDropSelectedIndex}
            setSelected={setAttendantRoleDropSelected}
            editDropdown={attendantRoleName}
          />
        </ContainerInputs>
      </ContainerBody>
    </Container>
  );
};
